import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input } from '@ds/forms';
import { dataSimples, diaIso, moeda } from '@/comuns/formato';
import { dados } from '@/data/porta';
import { valoresDasParcelas, vencimentoDaParcela } from '@/data/financeiroAvulso';
import { valorEmVigor } from '@/data/preferencias';
import type { CondicaoDeCobranca, Orcamento } from '@/data/tipos';

export interface AprovarOrcamentoModalProps {
    orcamento: Orcamento;
    onFechar: () => void;
    /** A tela chama o `decidirOrcamento` e diz se deu certo. */
    onAprovar: (cobranca: CondicaoDeCobranca) => Promise<boolean>;
}

/**
 * Aprovar é também dizer como a venda será paga: as parcelas entram em contas a
 * receber na mesma transação da aprovação (0175). A categoria é a de receitas
 * padrão das preferências — quem aprova pode ser a recepção, que não enxerga o
 * cadastro do financeiro.
 */
export function AprovarOrcamentoModal({ orcamento, onFechar, onAprovar }: AprovarOrcamentoModalProps) {
    const idParcelas = useId();
    const idPrimeiro = useId();
    const [gerar, setGerar] = useState(true);
    const [parcelasTexto, setParcelasTexto] = useState('1');
    const [primeiro, setPrimeiro] = useState(diaIso());
    const [categoriaId, setCategoriaId] = useState<string | undefined>(undefined);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        let vivo = true;
        /* Sem a preferência a parcela nasce sem categoria — e isso não impede aprovar. */
        dados().preferencias()
            .then((salvas) => {
                const padrao = valorEmVigor('fin.cat-receitas', salvas);
                if (vivo && typeof padrao === 'string' && padrao) setCategoriaId(padrao);
            })
            .catch(() => { /* a categoria é opcional: sem ela, aprova do mesmo jeito */ });
        return () => { vivo = false; };
    }, []);

    const parcelas = Number(parcelasTexto);
    const parcelasOk = Number.isInteger(parcelas) && parcelas >= 1 && parcelas <= 48 && parcelas <= Math.max(1, orcamento.totalCentavos);
    const dataOk = /^\d{4}-\d{2}-\d{2}$/.test(primeiro);
    const podeAprovar = !gerar || orcamento.totalCentavos === 0 || (parcelasOk && dataOk);
    const previa = gerar && parcelasOk && dataOk && orcamento.totalCentavos > 0
        ? valoresDasParcelas(orcamento.totalCentavos, parcelas).map((valor, i) => ({ valor, vencimento: vencimentoDaParcela(primeiro, i + 1) }))
        : [];

    const aprovar = async () => {
        if (!podeAprovar) return;
        setSalvando(true);
        const ok = await onAprovar({
            gerar, parcelas: parcelasOk ? parcelas : 1, primeiroVencimento: primeiro,
            ...(categoriaId ? { categoriaId } : {}),
        });
        setSalvando(false);
        if (ok) onFechar();
    };

    return (
        <Modal
            open width={520} onClose={onFechar}
            title={`Aprovar ${orcamento.numero}`}
            description={`${orcamento.paciente} · ${moeda(orcamento.totalCentavos)}`}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void aprovar(); }} disabled={!podeAprovar} loading={salvando}>Aprovar</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Checkbox
                    checked={gerar} onChange={setGerar}
                    label="Lançar em contas a receber"
                    description="Desmarque se a clínica cobra esta venda por fora do sistema."
                />
                {gerar && orcamento.totalCentavos > 0 && (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <FormField label="Parcelas" htmlFor={idParcelas} required error={parcelasTexto.trim() && !parcelasOk ? 'De 1 a 48' : undefined}>
                                <Input id={idParcelas} value={parcelasTexto} onChange={setParcelasTexto} inputMode="numeric" invalid={Boolean(parcelasTexto.trim()) && !parcelasOk} />
                            </FormField>
                            <FormField label="Primeiro vencimento" htmlFor={idPrimeiro} required>
                                <Input id={idPrimeiro} type="date" value={primeiro} onChange={setPrimeiro} />
                            </FormField>
                        </div>
                        {previa.length > 0 && (
                            <ul aria-label="Parcelas que serão lançadas" style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                                {previa.map((p, i) => (
                                    <li key={p.vencimento + String(i)} style={{ display: 'flex', gap: 12, padding: '8px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)', fontSize: 13 }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>{i + 1}/{previa.length}</span>
                                        <span style={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>{dataSimples(p.vencimento)}</span>
                                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(p.valor)}</strong>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
                {!gerar && (
                    <Alert tone="warning" dismissible={false}>
                        Nada entra no financeiro. Se mudar de ideia, lance o título à mão em Financeiro › Novo lançamento.
                    </Alert>
                )}
            </div>
        </Modal>
    );
}
